import { FC, useEffect, useRef } from 'react';
import styles from './styles.module.scss';
import { Box, Typography } from '@mui/material';
import { Container } from '@/components';
import { Trans, useTranslation } from 'next-i18next';
import Icon1 from './assets/1.svg';
import Icon2 from './assets/2.svg';
import Icon3 from './assets/3.svg';
import {
  PreferenceItem,
  PreferenceItemAnimation,
  PreferenceItemImage,
  PreferenceItemType,
} from './Item';
import prefImage1 from '/public/images/ourData/pref1.webp';
import prefImage2 from '/public/images/ourData/pref2.webp';
import prefImage3 from '/public/images/ourData/pref3.webp';
import clsx from 'clsx';
import { isMobile } from '@/libs/isMobile';

export const PreferenceSection: FC = () => {
  const { t } = useTranslation('ourData');
  const scrollBarRef = useRef<HTMLElement>(null);
  const config: (PreferenceItemImage | PreferenceItemAnimation)[] = [
    {
      title: t('pref.infoRow1.title'),
      description: {
        key: 'pref.infoRow1.description',
      },
      type: PreferenceItemType.Image,
      imageSrc: prefImage1,
      Icon: Icon1,
      id: 'infoRow1',
      imageStyle: {
        transform: 'scale(0.95)',
      },
    },
    {
      title: t('pref.infoRow2.title'),
      description: {
        key: 'pref.infoRow2.description',
      },
      type: PreferenceItemType.Image,
      imageSrc: prefImage2,
      Icon: Icon2,
      id: 'infoRow2',
    },
    {
      title: t('pref.infoRow3.title'),
      description: {
        key: 'pref.infoRow3.description',
      },
      type: PreferenceItemType.Image,
      imageSrc: prefImage3,
      Icon: Icon3,
      id: 'infoRow3',
      list: [
        t('pref.infoRow3.list.fullName'),
        t('pref.infoRow3.list.emails'),
        t('pref.infoRow3.list.jobTitle'),
        t('pref.infoRow3.list.phone'),
        t('pref.infoRow3.list.company'),
        t('pref.infoRow3.list.state'),
        t('pref.infoRow3.list.seniority'),
        t('pref.infoRow3.list.country'),
      ],
      imageStyle: {
        transform: 'scale(0.99)',
      },
    },
  ];

  const handleScroll = (): void => {
    if (isMobile()) return;

    const scrollBarRect = (scrollBarRef.current as HTMLElement).getBoundingClientRect();
    const scrollH = scrollBarRect.y - window.innerHeight;
    if (scrollH <= 0) {
      const gradient = `linear-gradient(to bottom, #ff5001, #F5F6F8 ${Math.floor(
        Math.abs(scrollH) / (scrollBarRect.height / 100),
      )}%)`;
      (scrollBarRef.current as HTMLElement).style.background = gradient;
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Box component="section" className={styles.section}>
      <Container className={styles.container}>
        <Typography variant="h2" className={styles.sectionTitle}>
          <Trans ns="ourData" i18nKey="pref.title" />
        </Typography>
        <Typography className={styles.sectionSubTitle}>
          <Trans ns="ourData" i18nKey="pref.subtitle" />
        </Typography>

        <Box className={styles.body}>
          <Box className={styles.rowGroup}>
            <Box className={clsx(styles.scrollbar, 'isDesktop')} ref={scrollBarRef} />
            {[config[0], config[1]].map((item) => {
              return <PreferenceItem key={item.id} item={item} />;
            })}
          </Box>
          {[config[2]].map((item) => {
            return <PreferenceItem key={item.id} item={item} />;
          })}
        </Box>
      </Container>
    </Box>
  );
};
